import cn from '@integrations/classnames'
import type { ComponentProps, HTMLAttributes, ReactNode } from '@integrations/react'
import { PAGE_FILL, PageFillScroll } from '../../Layout/PageShell'
import MMPagination from '../../WebComponents/MMPagination'

interface Props extends Omit<HTMLAttributes<HTMLDivElement>, 'children'> {
  /** The table (`MMTableView.Table`). */
  children?: ReactNode
  /** Above the table, inside the card (a filter, a toolbar). */
  header?: ReactNode
  /** Shown instead of the table and the page controls: a spinner, an error, an empty state. */
  fallback?: ReactNode
  /** Under the table: the page controls (`pagination.register()`). */
  pagination?: ComponentProps<typeof MMPagination>
  /** Under the pagination: a summary line, an export button. */
  footer?: ReactNode
  /** Props of the scrolling part, for a list that needs its own class or `data-test`. */
  scrollProps?: HTMLAttributes<HTMLDivElement> & { 'data-test'?: string }
}

/**
 * The list card every `fill` page shows its table in: one card that fills the page, the table scrolling inside it
 * (header sticky, rows of equal height, the drawn scrollbar), the page controls and a footer under it. While there is
 * nothing to list (loading, an error, an empty list) `fallback` takes the place of the table.
 */
function TableCard({ children, header, fallback, pagination, footer, scrollProps, className, ...props }: Props) {
  return (
    <div className={cn('table-surface', PAGE_FILL.card, className)} {...props}>
      {header}
      {fallback || (
        <>
          <PageFillScroll {...scrollProps} className={cn(PAGE_FILL.rows, scrollProps?.className)}>
            {children}
          </PageFillScroll>
          {pagination && <MMPagination className={cn('table-pagination')} {...pagination} />}
        </>
      )}
      {footer}
    </div>
  )
}

export default TableCard
